import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext, useDraggable, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { Square, Circle, RectangleHorizontal, Save, RotateCcw, Users, Edit2, Move } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Resolution-independent logical coordinate space. Tables are stored in these
// integer units (reusing existing position_x/position_y ints) so a layout
// designed on desktop renders proportionally on any tablet/screen width.
const LOGICAL_W = 1000;
const LOGICAL_H = 600;
const NODE_PX = 84; // fixed physical node size (keeps tables legible on every device)

const SHAPE_ICON = { square: Square, round: Circle, rectangle: RectangleHorizontal };

const STATUS_RING = {
  available: 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10',
  occupied: 'border-red-400 bg-red-50 dark:bg-red-500/10',
  reserved: 'border-amber-400 bg-amber-50 dark:bg-amber-500/10',
  cleaning: 'border-blue-400 bg-blue-50 dark:bg-blue-500/10',
};

// A table is "unplaced" when it still carries the default 0,0 coordinate.
const isUnplaced = (t) => !(t.position_x > 0 || t.position_y > 0);

// Lay out unplaced tables into a tidy non-overlapping grid (frontend only —
// never written unless the manager drags + saves).
function computeInitialPositions(tables) {
  const pos = {};
  const unplaced = [];
  tables.forEach((t) => {
    if (isUnplaced(t)) unplaced.push(t);
    else pos[t.id] = { x: t.position_x, y: t.position_y };
  });
  const cols = 6;
  const cellW = (LOGICAL_W - 40) / cols;
  const rowH = 130;
  unplaced.forEach((t, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    pos[t.id] = { x: Math.round(20 + c * cellW), y: Math.round(30 + r * rowH) };
  });
  return pos;
}

function DraggableTable({ table, pos, scale, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: table.id });
  const ShapeIcon = SHAPE_ICON[table.shape] || Square;
  const ring = STATUS_RING[table.status] || STATUS_RING.available;

  // Clamp the rendered position so a node can never visually spill outside the
  // canvas on a narrower screen than it was designed on.
  const maxLeft = LOGICAL_W * scale - NODE_PX;
  const maxTop = LOGICAL_H * scale - NODE_PX;
  const left = Math.max(0, Math.min(pos.x * scale, maxLeft));
  const top = Math.max(0, Math.min(pos.y * scale, maxTop));

  const style = {
    position: 'absolute',
    left, top,
    width: NODE_PX, height: NODE_PX,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    zIndex: isDragging ? 50 : 1,
    touchAction: 'none',
  };
  const rounded = table.shape === 'round' ? 'rounded-full' : 'rounded-lg';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-2 ${ring} ${rounded} shadow-sm select-none flex flex-col items-center justify-center ${isDragging ? 'shadow-xl scale-105 opacity-90' : ''}`}
      data-testid={`floor-table-${table.table_number}`}
      {...listeners}
      {...attributes}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(table); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1 right-1 p-0.5 rounded hover:bg-black/5"
        data-testid={`floor-edit-${table.table_number}`}
      >
        <Edit2 size={12} className="text-muted-foreground" />
      </button>
      <ShapeIcon size={16} className="text-muted-foreground mb-0.5" />
      <span className="font-bold text-sm leading-none dark:text-white">{table.table_number}</span>
      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground mt-0.5">
        <Users size={10} />{table.capacity}
      </span>
    </div>
  );
}

export function FloorDesigner({ tables, sectionLabel, onEditTable, onSaveLayout }) {
  const containerRef = useRef(null);
  const [canvasW, setCanvasW] = useState(0);
  const [positions, setPositions] = useState({});
  const [dirty, setDirty] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  // Re-seed positions whenever the visible table set changes (e.g. section switch).
  const tableKey = useMemo(
    () => tables.map((t) => `${t.id}:${t.position_x},${t.position_y}`).join('|'),
    [tables],
  );
  useEffect(() => {
    setPositions(computeInitialPositions(tables));
    setDirty(new Set());
  }, [tableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure the canvas width to derive the logical→pixel scale (responsive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setCanvasW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = canvasW ? canvasW / LOGICAL_W : 0;
  const canvasH = canvasW * (LOGICAL_H / LOGICAL_W);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, delta } = event;
    if (!scale || (!delta.x && !delta.y)) return;
    const id = active.id;
    setPositions((prev) => {
      const cur = prev[id] || { x: 0, y: 0 };
      // Clamp within logical bounds accounting for the fixed node footprint.
      const nodeLogical = NODE_PX / scale;
      const maxX = Math.max(0, LOGICAL_W - nodeLogical);
      const maxY = Math.max(0, LOGICAL_H - nodeLogical);
      const nx = Math.round(Math.max(0, Math.min(cur.x + delta.x / scale, maxX)));
      const ny = Math.round(Math.max(0, Math.min(cur.y + delta.y / scale, maxY)));
      return { ...prev, [id]: { x: nx, y: ny } };
    });
    setDirty((prev) => new Set(prev).add(id));
  }, [scale]);

  const handleReset = () => {
    setPositions(computeInitialPositions(tables));
    setDirty(new Set());
  };

  const handleSave = async () => {
    if (!dirty.size) return;
    const moved = [...dirty].map((id) => ({
      id,
      position_x: positions[id]?.x ?? 0,
      position_y: positions[id]?.y ?? 0,
    }));
    setSaving(true);
    try {
      await onSaveLayout(moved);
      setDirty(new Set());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="floor-designer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Move size={14} />
          <span>Drag tables to arrange · {sectionLabel}</span>
          {dirty.size > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-400" data-testid="floor-unsaved-badge">
              {dirty.size} unsaved
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            onClick={handleReset}
            disabled={dirty.size === 0 || saving}
            data-testid="floor-reset-btn"
          >
            <RotateCcw size={14} className="mr-1" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={dirty.size === 0 || saving}
            data-testid="floor-save-layout-btn"
          >
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save Layout'}
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div
          ref={containerRef}
          className="relative w-full rounded-xl border-2 border-dashed border-muted bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[length:40px_40px] overflow-hidden"
          style={{ height: canvasH || 360 }}
          data-testid="floor-canvas"
        >
          {tables.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              No tables in this section
            </div>
          ) : (
            scale > 0 && tables.map((t) => (
              <DraggableTable
                key={t.id}
                table={t}
                pos={positions[t.id] || { x: 0, y: 0 }}
                scale={scale}
                onEdit={onEditTable}
              />
            ))
          )}
        </div>
      </DndContext>
    </div>
  );
}
